import React, { useCallback, useState } from 'react';
import { BiSpreadsheet } from 'react-icons/bi';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Text,
  VStack,
  Button,
  useDisclosure,
  Input,
  Image,
  Icon,
  HStack,
} from '@chakra-ui/react';
import {
  useAppSelector,
  useCheckMultiplePlatesMutation,
  setFile,
  useAppDispatch,
  setMultiple,
} from '@spartalabs/pdc-core';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import ScheduleFile from 'assets/files/Planilha_Modelo.xlsx';
import trashSvg from 'assets/svg/trash.svg';
import { Card, Unauthorized } from 'components';
import { useCustomToast } from 'hooks';
import { DelayAnimation } from 'shared/DelayAnimation';
import { FileModal } from '../Modals/FileModal';
import { NewScheduleHeader } from '../NewScheduleHeader';
interface ImageProps {
  file: File | null;
  name: string;
  path: string | null;
}

export const MultipleForm: React.FC = () => {
  const managerId = useAppSelector(state => state.authSlicer.user.managerId);
  const navigate = useNavigate();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const dispatch = useAppDispatch();
  const { handleToast } = useCustomToast();
  const [sheet, setSheet] = useState<ImageProps | null>(null);

  const [checkPlates, { data, isLoading }] = useCheckMultiplePlatesMutation();

  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const handleSubmit = useCallback(async () => {
    if (!sheet?.file) return;
    try {
      const formData = new FormData();

      formData.append('arquivo', sheet.file);
      formData.append('payload', JSON.stringify({ gestorId: managerId }));

      const multiple = await checkPlates(formData).unwrap();

      dispatch(setFile(sheet?.file));

      if (multiple && multiple?.plates?.length > 0) {
        dispatch(setMultiple(multiple));

        onOpen();
      } else throw new Error();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleToast({
        step: 4,
        action: 'enviar sua planilha',
        helpText: 'Ocorreu um erro inesperado,verifique a planilha enviada',
      });

      if (error.errors) {
        dispatch(setMultiple(error));

        navigate('/novo-agendamento/revisao');
      }
    }
  }, [sheet]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;

    if (selectedFiles) {
      const newPreviewImages = Array.from(selectedFiles).map(file => {
        const reader = new FileReader();

        return new Promise<string>((resolve, reject) => {
          reader.onload = function (event) {
            if (event.target?.result) {
              resolve(event.target.result.toString());
            } else {
              reject(new Error('Failed to read the file.'));
            }
          };

          reader.onerror = function (event) {
            reject(
              event.target?.error ?? new Error('Failed to read the file.'),
            );
          };

          reader.readAsDataURL(file as Blob);
        });
      });

      Promise.all(newPreviewImages)
        .then(result => {
          const names = Array.from(selectedFiles).map(
            (file: File) => file.name,
          );
          setSheet({
            file: selectedFiles[0],
            name: names[0],
            path: result[0],
          });
        })
        .catch(() =>
          handleToast({
            step: 4,
            action: 'fazer o upload do seu arquivo',
          }),
        );
    }
  };

  if (
    !permissions?.includes('createSchedule') ||
    !permissions?.includes('multiple')
  )
    return <Unauthorized />;

  return (
    <DelayAnimation>
      <NewScheduleHeader isMultiple step={1} />
      <FileModal total={data?.sentVehicles} isOpen={isOpen} onClose={onClose} />
      <VStack
        w="100%"
        display="flex"
        align="center"
        justify="center"
        pt="44px"
        pb="44px"
      >
        <Flex flexDir="column" align="center" justify="center" mb="32px">
          <Text fontSize="18px" color="text.100">
            Dados sobre o veículo para agendamento
          </Text>
        </Flex>

        <Card mt="24px" minH="96px" maxW="400px" w="100%">
          <Flex
            mt="8px"
            w="100%"
            align="center"
            justify="space-between"
            borderColor="text.100"
            borderStyle="dashed"
            borderWidth="1px"
            rounded="md"
            shadow="sm"
            role="group"
            py="15px"
            px="24px"
            h="54px"
            transition="all 150ms ease-in-out"
            _hover={{
              shadow: 'md',
            }}
            position="relative"
          >
            <Input
              cursor="pointer"
              type="file"
              h="100%"
              width="100%"
              position="absolute"
              multiple
              top="0"
              left="0"
              bottom="0"
              opacity="0"
              aria-hidden="true"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              border="3px solid red"
              onChange={e => handleFileChange(e)}
            />
            <Text fontSize="12px" color="text.100">
              Abertura múltiplas placas
            </Text>

            <Flex bg="grey.200" py="8px" px="10px" borderRadius="8px">
              <Text fontSize="12px" fontWeight="normal" color="black">
                Upload
              </Text>
            </Flex>
          </Flex>
          <Text fontSize="14px" color="text.300" mt="16px">
            IMPORTANTE: O uso da planilha modelo é obrigatório para validações
            dos dados
          </Text>
          {sheet?.path && (
            <Flex mt="18px" w="100%" align="center">
              <Icon
                aria-hidden="true"
                as={BiSpreadsheet}
                color="button"
                h="20px"
                w="20px"
                alignSelf="center"
              />

              <Text noOfLines={1} maxW="260px" ml="8px" color="black">
                {sheet?.name}
              </Text>
              <Button
                data-cy="remove-image"
                onClick={() => setSheet({ name: '', path: null, file: null })}
                bg="transparent"
                p="0"
                _hover={{ bg: 'transparent' }}
              >
                <Image alt="remover logo" src={trashSvg} />
              </Button>
            </Flex>
          )}

          <Button
            as="a"
            target="_blank"
            download="PlanilhaModelo"
            href={ScheduleFile}
            mt="24px"
            border="1px solid"
            borderColor="blue.0"
            textColor="blue.0"
            fontWeight={500}
            variant="outline"
          >
            Baixar planilha modelo
          </Button>
          <HStack mt="28px" w="100%" justifyContent="flex-end">
            <Button
              onClick={() => navigate('/novo-agendamento/veiculos')}
              variant="outline"
            >
              Voltar
            </Button>
            <Button
              isLoading={isLoading}
              isDisabled={!sheet?.file}
              onClick={handleSubmit}
            >
              Continuar
            </Button>
          </HStack>
        </Card>
      </VStack>
    </DelayAnimation>
  );
};
